/**
 * 画廊页面 - Airbnb 风格
 * 按相册分 section，图文混排，杂志感布局
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getAlbums, getAlbumTags, type Album, type Photo } from '../src/api/albums';

const GalleryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const [albums, setAlbums] = useState<Album[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxPhoto, setLightboxPhoto] = useState<{ photos: Photo[]; index: number } | null>(null);

  useEffect(() => {
    document.title = `${t('gallery.title')} - ${t('html.title')}`;
    loadData();
  }, [t]);

  useEffect(() => {
    loadAlbums();
  }, [activeTag]);

  const loadData = async () => {
    try {
      const [albumsData, tagsData] = await Promise.all([getAlbums(), getAlbumTags()]);
      setAlbums(albumsData);
      setTags(tagsData);
    } catch (err) {
      console.error('Failed to load gallery:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAlbums = async () => {
    try {
      const data = await getAlbums(activeTag || undefined);
      setAlbums(data);
    } catch (err) {
      console.error('Failed to load albums:', err);
    }
  };

  const getApiBaseUrl = () => {
    return import.meta.env.VITE_NODE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8084';
  };

  const getImageUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${getApiBaseUrl()}${path}`;
  };

  const openLightbox = (photos: Photo[], index: number) => {
    setLightboxPhoto({ photos, index });
  };

  const getTitle = (album: Album) => isZh ? album.title : (album.titleEn || album.title);
  const getDesc = (album: Album) => isZh ? album.description : (album.descriptionEn || album.description);
  const getPhotoTitle = (photo: Photo) => isZh ? (photo.title || '') : (photo.titleEn || photo.title || '');

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-24 pb-6 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            {t('gallery.title')}
          </h1>
          <p className="text-base text-slate-500">
            {t('gallery.subtitle')}
          </p>
        </div>
      </section>

      {/* 标签筛选 */}
      {tags.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 pb-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setActiveTag(null)}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                !activeTag
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {t('gallery.all')}
            </button>
            {tags.map(tag => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeTag === tag
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 相册内容 */}
      {isLoading ? (
        <div className="max-w-6xl mx-auto px-6 space-y-16 pb-24">
          {[1, 2].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-[400px] bg-slate-100 rounded-3xl mb-6" />
              <div className="h-6 bg-slate-100 rounded w-1/3 mb-3" />
              <div className="h-4 bg-slate-50 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : albums.length === 0 ? (
        <div className="text-center py-24">
          <div className="text-6xl mb-4">📷</div>
          <p className="text-slate-400 text-lg">{t('gallery.empty')}</p>
        </div>
      ) : (
        <div className="pb-24">
          {albums.map((album, albumIdx) => {
            const photos = album.photos || [];
            if (photos.length === 0) return null;
            const layout = albumIdx === 0 ? 0 : ((albumIdx - 1) % 2 === 0 ? 1 : 2); // 第一个全宽，后面左右交替
            const coverPhoto = photos[0];
            const restPhotos = photos.slice(1);

            return (
              <section key={album.id} className="max-w-6xl mx-auto px-6 py-12 border-b border-slate-200 last:border-b-0">
                {/* 布局 0: 全宽大图 + 下方小图 */}
                {layout === 0 && (
                  <>
                    {/* 大图 */}
                    <div
                      className="relative rounded-3xl overflow-hidden cursor-pointer group mb-8"
                      onClick={() => openLightbox(photos, 0)}
                    >
                      <img
                        src={getImageUrl(coverPhoto.url)}
                        alt={getPhotoTitle(coverPhoto)}
                        className="w-full aspect-[16/9] object-cover group-hover:scale-[1.02] transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    {/* 标题描述 */}
                    <div className="mb-8">
                      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">{getTitle(album)}</h2>
                      {getDesc(album) && (
                        <p className="text-lg text-slate-500">{getDesc(album)}</p>
                      )}
                    </div>
                    {/* 小图 */}
                    {restPhotos.length > 0 && (
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                        {restPhotos.slice(0, 8).map((photo, idx) => (
                          <div
                            key={photo.id}
                            className="relative rounded-xl overflow-hidden cursor-pointer group aspect-[3/2]"
                            onClick={() => openLightbox(photos, idx + 1)}
                          >
                            <img
                              src={getImageUrl(photo.thumbUrl || photo.url)}
                              alt={getPhotoTitle(photo)}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* 布局 1: 左文右图 */}
                {layout === 1 && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-8">
                      {/* 左侧文字 */}
                      <div>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{getTitle(album)}</h2>
                        {getDesc(album) && (
                          <p className="text-lg text-slate-500 leading-relaxed">{getDesc(album)}</p>
                        )}
                        {album.tags && album.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-6">
                            {album.tags.map(tag => (
                              <span key={tag} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* 右侧大图 */}
                      <div
                        className="relative rounded-3xl overflow-hidden cursor-pointer group"
                        onClick={() => openLightbox(photos, 0)}
                      >
                        <img
                          src={getImageUrl(coverPhoto.url)}
                          alt={getPhotoTitle(coverPhoto)}
                          className="w-full aspect-[4/3] object-cover group-hover:scale-[1.02] transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      </div>
                    </div>
                    {/* 小图 */}
                    {restPhotos.length > 0 && (
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                        {restPhotos.slice(0, 8).map((photo, idx) => (
                          <div
                            key={photo.id}
                            className="relative rounded-xl overflow-hidden cursor-pointer group aspect-[3/2]"
                            onClick={() => openLightbox(photos, idx + 1)}
                          >
                            <img
                              src={getImageUrl(photo.thumbUrl || photo.url)}
                              alt={getPhotoTitle(photo)}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* 布局 2: 右文左图 */}
                {layout === 2 && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-8">
                      {/* 左侧大图 */}
                      <div
                        className="relative rounded-3xl overflow-hidden cursor-pointer group order-2 lg:order-1"
                        onClick={() => openLightbox(photos, 0)}
                      >
                        <img
                          src={getImageUrl(coverPhoto.url)}
                          alt={getPhotoTitle(coverPhoto)}
                          className="w-full aspect-[4/3] object-cover group-hover:scale-[1.02] transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      </div>
                      {/* 右侧文字 */}
                      <div className="order-1 lg:order-2">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{getTitle(album)}</h2>
                        {getDesc(album) && (
                          <p className="text-lg text-slate-500 leading-relaxed">{getDesc(album)}</p>
                        )}
                        {album.tags && album.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-6">
                            {album.tags.map(tag => (
                              <span key={tag} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* 小图 */}
                    {restPhotos.length > 0 && (
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                        {restPhotos.slice(0, 8).map((photo, idx) => (
                          <div
                            key={photo.id}
                            className="relative rounded-xl overflow-hidden cursor-pointer group aspect-[3/2]"
                            onClick={() => openLightbox(photos, idx + 1)}
                          >
                            <img
                              src={getImageUrl(photo.thumbUrl || photo.url)}
                              alt={getPhotoTitle(photo)}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      <Lightbox
        data={lightboxPhoto}
        onClose={() => setLightboxPhoto(null)}
        getImageUrl={getImageUrl}
        isZh={isZh}
      />
    </div>
  );
};

/**
 * Lightbox 组件
 */
const Lightbox: React.FC<{
  data: { photos: Photo[]; index: number } | null;
  onClose: () => void;
  getImageUrl: (path: string) => string;
  isZh: boolean;
}> = ({ data, onClose, getImageUrl, isZh }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (data) setIndex(data.index);
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex(i => (i > 0 ? i - 1 : data.photos.length - 1));
      if (e.key === 'ArrowRight') setIndex(i => (i < data.photos.length - 1 ? i + 1 : 0));
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [data, onClose]);

  if (!data || !data.photos[index]) return null;

  const photo = data.photos[index];
  const title = isZh ? (photo.title || '') : (photo.titleEn || photo.title || '');

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      {/* 关闭 */}
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 20, right: 20, color: 'rgba(255,255,255,0.8)', background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
      >
        <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* 上一张 */}
      {data.photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIndex(i => (i > 0 ? i - 1 : data.photos.length - 1)); }}
          style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* 图片 */}
      <img
        src={getImageUrl(photo.url)}
        alt={title}
        style={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* 下一张 */}
      {data.photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIndex(i => (i < data.photos.length - 1 ? i + 1 : 0)); }}
          style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* 底部信息 */}
      <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
        <p style={{ fontSize: 14 }}>{index + 1} / {data.photos.length}</p>
        {title && <p style={{ fontSize: 14, marginTop: 4 }}>{title}</p>}
      </div>
    </div>,
    document.body
  );
};

export default GalleryPage;
